import { useState } from "react";
import { toast } from "sonner";
import { Star, ChevronDown } from "lucide-react";
import { api, auDate, errMsg } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

export function RatingsDialog({ contact, onClose }) {
  const [d, reload] = useGet(`/contacts/${contact.id}/ratings`);
  const [stars, setStars] = useState(null);
  const [comment, setComment] = useState(null);
  const [open, setOpen] = useState(false);
  const s = stars ?? d?.mine?.stars ?? 0;
  const c = comment ?? d?.mine?.comment ?? "";
  const save = async () => { try { await api.put(`/contacts/${contact.id}/ratings`, { stars: s, comment: c }); toast.success("Rating saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ratings-dialog">
        <DialogHeader><DialogTitle>{contact.company || `${contact.first_name} ${contact.last_name || ""}`}</DialogTitle></DialogHeader>
        <div className="text-sm text-muted-foreground" data-testid="ratings-average">{d?.average ? `Average ★ ${d.average} from ${d.count} rating${d.count > 1 ? "s" : ""}` : "No ratings yet"}</div>
        <div className="space-y-2 rounded-md border border-[#C2A462]/25 p-3">
          <div className="text-xs text-slate-400">Your rating</div>
          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setStars(n)} data-testid={`star-${n}`}><Star className={`h-6 w-6 ${n <= s ? "fill-[#C2A462] text-[#C2A462]" : "text-slate-600"}`} /></button>)}</div>
          <Textarea rows={3} placeholder="Optional comment to share with other staff" value={c} onChange={(e) => setComment(e.target.value)} data-testid="rating-comment" />
          <Button size="sm" onClick={save} disabled={!s} data-testid="rating-save">Save rating</Button>
        </div>
        <button className="flex items-center gap-1 text-sm text-[#C2A462]" onClick={() => setOpen(!open)} data-testid="ratings-comments-toggle"><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />Staff comments ({(d?.items || []).filter((x) => x.comment).length})</button>
        {open && <div className="max-h-60 space-y-2 overflow-y-auto">{(d?.items || []).filter((x) => x.comment).map((x) => (
          <div key={x.id} className="rounded border border-white/10 p-2 text-sm"><div className="text-xs text-slate-400">{x.user_name} · ★ {x.stars} · {auDate(x.updated_at)}</div>{x.comment}</div>))}</div>}
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ r, onClose, onDone }) {
  const [answers] = useGet(`/referrals/${r.id}/answers`);
  const [f, setF] = useState({ first_name: r.first_name, last_name: r.last_name, email: r.email, phone: r.phone, tab: "general" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async () => { try { await api.post(`/referrals/${r.id}/transfer`, f); toast.success("Referral transferred to Contacts"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="referral-review-dialog">
        <DialogHeader><DialogTitle>Review referral</DialogTitle></DialogHeader>
        <div className="rounded-md border border-white/10 p-3 text-sm space-y-1" data-testid="referral-answers">{(answers || []).map((a) => <div key={a.key} className="grid grid-cols-[40%_1fr] gap-2"><span className="text-muted-foreground">{a.label}</span><span>{Array.isArray(a.value) ? a.value.join(", ") : String(a.value ?? "—")}</span></div>)}</div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="First name" value={f.first_name} onChange={set("first_name")} data-testid="ref-first" /><Input placeholder="Last name" value={f.last_name} onChange={set("last_name")} data-testid="ref-last" />
          <Input placeholder="Email" value={f.email} onChange={set("email")} data-testid="ref-email" /><Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="ref-phone" />
          <select className="col-span-2 h-9 rounded-md border border-input bg-background px-2 text-sm" value={f.tab} onChange={set("tab")} data-testid="ref-tab"><option value="private">Private</option><option value="general">General</option><option value="subcontractor">Subcontractor</option></select>
        </div>
        <p className="text-[11px] text-slate-500">All answers are copied into the new contact's notes.</p>
        <Button onClick={go} disabled={!f.first_name} data-testid="ref-transfer-btn">Transfer to Contacts</Button>
      </DialogContent>
    </Dialog>
  );
}

export function ReferralsTab() {
  const [list, reload] = useGet("/referrals");
  const [review, setReview] = useState(null);
  const dismiss = async (r) => { if (!window.confirm("Dismiss this referral?")) return; await api.delete(`/referrals/${r.id}`); reload(); };
  return (
    <div className="space-y-3" data-testid="referrals-tab">
      <p className="text-sm text-muted-foreground">Submissions from forms set to "Referrals" land here. Review each one, then transfer it to Private, General or Subcontractor. The full answers are in Forms & Surveys → Submissions.</p>
      <div className="rounded-md border bg-card divide-y divide-white/5">
        {(list || []).map((r) => (
          <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`referral-${r.id}`}>
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold">{r.first_name} {r.last_name}</div><div className="text-xs text-muted-foreground">{[r.email, r.phone, r.form_name, auDate(r.created_at)].filter(Boolean).join(" · ")}</div></div>
            <Button size="sm" onClick={() => setReview(r)} data-testid={`referral-transfer-${r.id}`}>Review & transfer</Button>
            <Button size="sm" variant="ghost" onClick={() => dismiss(r)} data-testid={`referral-dismiss-${r.id}`}>Dismiss</Button>
          </div>
        ))}
        {!list?.length && <div className="p-8 text-center text-sm text-muted-foreground">No new referrals.</div>}
      </div>
      {review && <ReviewDialog r={review} onClose={() => setReview(null)} onDone={reload} />}
    </div>
  );
}
